import { DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";

import { useEffect } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import type { Gender } from "@/api/auth/auth.types";
import { LOGIN_KEY } from "@/hooks/api/login.hooks";
import { USER_KEYS, usePatchProfile } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn";
import { onError } from "@/utils/onError";

import Button from "../buttons/Button";
import { IconCheck } from "../icons";
import DateOfBirthInput from "../inputs/DateOfBirthInput";

const ChooseGender = () => {
  const {
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useFormContext<UserInfoForm>();

  const { t } = useTranslation();
  const gender = watch("gender");

  const genderOptions = [
    { gender: "M", text: t("Gender.male") },
    { gender: "W", text: t("Gender.female") },
  ];

  return (
    <>
      <label className="mb-2 block w-max">{t("Gender.choose")}</label>
      <div className="space-y-3">
        {genderOptions.map(({ gender: optionGender, text }) => (
          <Button
            key={optionGender}
            color="black"
            size="medium"
            className={cn(
              "flex flex-row-reverse items-center justify-between border border-transparent text-start transition-all hover:bg-surface-container-high active:bg-surface-container-high",
              {
                " border-orange-1": gender === optionGender,
              },
            )}
            icon={
              <IconCheck
                className={cn("size-6 !fill-transparent stroke-transparent transition-all", {
                  "stroke-orange-1": gender === optionGender,
                })}
              />
            }
            onClick={() => {
              setValue("gender", optionGender as "W" | "M");
              clearErrors("gender");
            }}
          >
            {text}
          </Button>
        ))}
      </div>
      {errors.gender && <p className="mt-2 text-sm text-red-500">{errors.gender.message}</p>}
    </>
  );
};

export interface UserInfoForm {
  date_of_birth: string;
  gender: Gender | null;
}

const UserInfoModal = () => {
  const form = useForm<UserInfoForm>();
  const { mutate, isPending } = usePatchProfile();
  const [params] = useSearchParams();
  const profileId = params.get("profile_id") ?? localStorage.getItem("splay:profileId");
  const { closeModal } = useModalRouter();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    if (!profileId) {
      closeModal();
    }
  }, [profileId]);

  if (!profileId) {
    return;
  }

  const onSubmit = (data: UserInfoForm) => {
    if (data.gender == null) {
      onError({ message: t("Gender.choose") });
      return;
    }

    mutate(
      {
        user_id: +profileId,
        //@ts-ignore
        data: {
          date_of_birth: data.date_of_birth,
          gender: data.gender,
        },
      },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries({ queryKey: LOGIN_KEY.profileList });
          await queryClient.invalidateQueries({ queryKey: USER_KEYS.profileDetail(+profileId) });
          closeModal();
        },
        onError,
      },
    );
  };

  return (
    <FormProvider {...form}>
      <div className={"w-[20rem] max-w-[100%] text-center"}>
        <DialogTitle as={"h2"} className={"staff-modal__title mb-5"}>
          {t("fill-profile")}
        </DialogTitle>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <DateOfBirthInput />
          <ChooseGender />
          <Button isLoading={isPending} type="submit">
            {t("Actions.save")}
          </Button>
        </form>
      </div>
    </FormProvider>
  );
};

export default UserInfoModal;
